import React from 'react';

import { updateWithToastPromise } from '@/clientOnlyUtils/clientUtils';
import { Popup } from '@poseidon/components/Popup';
import { Button, SingleCheckbox } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

import { useExportContactsCSVMutation } from '@/hooks/trpc/useContactMutation';
import { api } from '@/utils/api';
import { downloadAsCsv } from '@/utils/domUtils';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

/**
 * Field-group picker for the bulk-bar "Export CSV" action. Groups map to the
 * server's SELECTION_EXPORT_COLUMNS keys; the last selection persists in
 * localStorage so the second export is one click.
 */

type ExportColumn =
  | 'firstName'
  | 'lastName'
  | 'title'
  | 'company'
  | 'city'
  | 'linkedinUrl'
  | 'personalEmail'
  | 'professionalEmail'
  | 'phone'
  | 'tags'
  | 'background'
  | 'careerHighlights'
  | 'recentSignals'
  | 'wealthSignals'
  | 'talkingPoints'
  | 'contactId'
  | 'createdOn';

const GROUPS: { key: string; label: string; description: string; columns: ExportColumn[]; defaultOn: boolean }[] = [
  {
    key: 'basics',
    label: 'Basics',
    description: 'Name, title, company, city',
    columns: ['firstName', 'lastName', 'title', 'company', 'city'],
    defaultOn: true,
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    description: 'Profile URL',
    columns: ['linkedinUrl'],
    defaultOn: true,
  },
  {
    key: 'contactInfo',
    label: 'Contact info',
    description: 'Emails and phone',
    columns: ['personalEmail', 'professionalEmail', 'phone'],
    defaultOn: false,
  },
  {
    key: 'dossier',
    label: 'Dossier',
    description: 'Background, highlights, signals, talking points',
    columns: ['background', 'careerHighlights', 'recentSignals', 'wealthSignals', 'talkingPoints'],
    defaultOn: true,
  },
  {
    key: 'meta',
    label: 'Meta',
    description: 'Tags, contact ID, created date',
    columns: ['tags', 'contactId', 'createdOn'],
    defaultOn: false,
  },
];

const STORAGE_KEY = 'contactsExportGroups';

function loadSavedGroups(): Set<string> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw != null) return new Set(JSON.parse(raw) as string[]);
  } catch {
    // fall through to defaults
  }
  return new Set(GROUPS.filter((group) => group.defaultOn).map((group) => group.key));
}

export function ExportContactsModal({
  teamId,
  contactIds,
  onClose,
}: {
  teamId: string;
  contactIds: string[];
  onClose: () => void;
}) {
  const [selectedGroups, setSelectedGroups] = React.useState<Set<string>>(loadSavedGroups);
  const exportMutation = useExportContactsCSVMutation();

  // Live dossier coverage — the finish line for "generate dossiers → export".
  // Polls while the modal is open and some dossiers are still missing, so the
  // count visibly climbs as the worker completes contacts.
  const coverageQuery = api.contact.dossierCoverage.useQuery(
    { teamId, contactIds },
    { refetchInterval: (query) => (query.state.data && query.state.data.withDossier < query.state.data.total ? 3_000 : false) },
  );
  const coverage = coverageQuery.data;
  const dossierGroupSelected = selectedGroups.has('dossier');

  const toggleGroup = (key: string) =>
    setSelectedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...next]));
      } catch {
        // persistence is best-effort
      }
      return next;
    });

  const columns = GROUPS.filter((group) => selectedGroups.has(group.key)).flatMap((group) => group.columns);

  const onExport = async () => {
    const result = await updateWithToastPromise({
      loading: 'Exporting CSV…',
      success: 'CSV exported',
      mutationFnAsync: () => exportMutation.mutateAsync({ teamId, contactIds, columns }),
    });
    downloadAsCsv(result.filename, result.csv);
    onClose();
  };

  return (
    <Popup onClose={onClose} className="w-[26rem] text-left">
      <div className="flex flex-col gap-0.5">
        <span className="text-base font-semibold">
          Export {contactIds.length} contact{contactIds.length === 1 ? '' : 's'}
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">Pick the column groups to include.</span>
      </div>

      <div className="flex flex-col">
        {GROUPS.map((group) => (
          <div
            key={group.key}
            onClick={() => toggleGroup(group.key)}
            className="-mx-2 flex cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-gray-100 dark:hover:bg-gray-700/60">
            <SingleCheckbox size="sm" checked={selectedGroups.has(group.key)} className="mt-0.5 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-medium leading-5">{group.label}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400">{group.description}</div>
            </div>
          </div>
        ))}
      </div>

      {dossierGroupSelected && coverage != null && (
        <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900/40">
          {coverage.withDossier < coverage.total && <WaveSpinner size={14} className="mt-1 shrink-0" />}
          <span
            className={
              coverage.withDossier < coverage.total
                ? 'text-gray-600 dark:text-gray-300'
                : 'font-medium text-green-700 dark:text-green-400'
            }>
            Dossiers ready: {coverage.withDossier} / {coverage.total}
            {coverage.withDossier < coverage.total && (
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Still generating — export now and the missing ones ship blank, or wait for the count to finish.
              </span>
            )}
          </span>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="blue"
          icon={<General.Download01 />}
          disabled={columns.length === 0 || exportMutation.isPending}
          onClick={() => void onExport()}>
          {exportMutation.isPending ? 'Exporting…' : 'Export CSV'}
        </Button>
      </div>
    </Popup>
  );
}
